import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLanguageChange from "../../hooks/useLanguageChange";
import CenterModal from "../UI/Modal/CenterModal";
import {
    setIsAndroidBrowserCheckModalOpen,
    setIsAndroidWebViewModalOpen,
    setHasUserAcceptedWebView,
    setIsIOSBrowserCheckModalOpen,
    setIsLowerVersionModalOpen,
    setIsUnSupportedBrowserModalOpen,
    setIsWebViewFoundModalOpen,
    setShouldAutoAcceptPrivacyPolicy,
} from "../../reducers/common-reducer";
import chromeLogo from "../../assets/images/chrome.svg";
import safariLogo from "../../assets/images/safari.svg";
import firefoxLogo from "../../assets/images/firefox.png";
import { HELP_ARTICLE } from "../../constants/LDConstants";
import { GLOBAL_MEETING_DATA } from "../../constants/PublicVariables";
import { CopyTextOrLinkToClipboard, goToPlayStore, pageUrl } from "../../shared/utility";
import { SUPPORT_BROWSER_LINKS } from "../../constants/Constants";

const UnSupportedBrowserModal = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const {
        isUnSupportedBrowserModalOpen,
        isLowerVersionModalOpen,
        isIOSBrowserCheckModalOpen,
        isWebViewFoundModalOpen,
        isAndroidBrowserCheckModalOpen,
        isAndroidWebViewModalOpen,
    } = useSelector((state) => state.commonReducer);

    // <!----------------------- web unsupported browser Pop-up ------------------------------->
    const handleCloseUnsupportedBrowserModal = () => {
        dispatch(setIsUnSupportedBrowserModalOpen(false));
    };

    useEffect(() => {
        if (
            isUnSupportedBrowserModalOpen ||
            isLowerVersionModalOpen ||
            isIOSBrowserCheckModalOpen ||
            isWebViewFoundModalOpen ||
            isAndroidBrowserCheckModalOpen ||
            isAndroidWebViewModalOpen
        ) {
            const loader = document.getElementById("page-loader");
            if (loader) {
                loader.classList?.add("page-loader-hide");
            }
        }
    }, [
        isUnSupportedBrowserModalOpen,
        isLowerVersionModalOpen,
        isIOSBrowserCheckModalOpen,
        isWebViewFoundModalOpen,
        isAndroidBrowserCheckModalOpen,
        isAndroidWebViewModalOpen,
    ]);

    const handleCopyLink = () => {
        CopyTextOrLinkToClipboard(pageUrl(), localisedValues?.link_copied);
    };

    const header = <h5 className='modal-title'>{localisedValues["unsupported_browser"]}</h5>;

    const body = (
        <>
            <div className='cms-modal-text'>
                <p>
                    {localisedValues["some_features_are_not_supported_in_this_browser"]}{" "}
                    {localisedValues["try_these_option_to_have_better_experience"]}{" "}
                    {HELP_ARTICLE.BROWSER_REQUIRED_TO_CONNECT_CALL && (
                        <>
                            <span>{localisedValues["see"]}</span>{" "}
                            <button
                                className='btn default-btn-link'
                                onClick={() => window.open(HELP_ARTICLE.BROWSER_REQUIRED_TO_CONNECT_CALL, "_blank")}
                            >
                                {localisedValues["supported_browser_versions"]}
                            </button>
                        </>
                    )}
                </p>
            </div>
            <div className='support-browser-list'>
                <div className='support-browser-col'>
                    <div className='support-browser-box'>
                        <div className='support-browser-image'>
                            <img src={safariLogo} alt='Safari' />
                        </div>
                        <div className='support-browser-link'>
                            <button
                                className='btn default-btn-link text-dec-none'
                                onClick={() => window.open(SUPPORT_BROWSER_LINKS.SAFARI, "_blank")}
                            >
                                Safari
                            </button>
                        </div>
                    </div>
                </div>
                <div className='support-browser-col'>
                    <div className='support-browser-box'>
                        <div className='support-browser-image'>
                            <img src={chromeLogo} alt='Chrome' />
                        </div>
                        <div className='support-browser-link'>
                            <button
                                className='btn default-btn-link text-dec-none'
                                onClick={() => window.open(SUPPORT_BROWSER_LINKS.CHROME, "_blank")}
                            >
                                Chrome
                            </button>
                        </div>
                    </div>
                </div>
                <div className='support-browser-col'>
                    <div className='support-browser-box'>
                        <div className='support-browser-image'>
                            <img src={firefoxLogo} alt='Firefox' />
                        </div>
                        <div className='support-browser-link'>
                            <button
                                className='btn default-btn-link text-dec-none'
                                onClick={() => window.open(SUPPORT_BROWSER_LINKS.FIREFOX, "_blank")}
                            >
                                Firefox
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const footer = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-primary' data-bs-dismiss='modal' onClick={handleCloseUnsupportedBrowserModal}>
                    {localisedValues["okay_cap"]}
                </button>
            </div>
        </div>
    );

    // <!----------------------- iOS & android lower version Pop-up ------------------------------->

    const handleCloseLowerVersionModal = () => {
        dispatch(setIsLowerVersionModalOpen(false));
    };

    const lowerVrsionHeader = <h5 className='modal-title'>{localisedValues["message_title"]}</h5>;

    const lowerVersionBody = (
        <div className='cms-modal-text'>
            <p>
                {localisedValues["device_not_compatible"]} {localisedValues["please_download_our_app"]}
            </p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues["note_colon"]}</strong>{" "}
                    <span>{localisedValues["host_features_will_not_be_available_when_using_app"]}</span>
                </p>
            )}
        </div>
    );

    const lowerVersionFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={handleCloseLowerVersionModal}>
                    {localisedValues["cancel_initcap"]}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={goToPlayStore}>
                    {localisedValues["download_app"]}
                </button>
            </div>
        </div>
    );

    // <!----------------------- iOS unsupported broweser Pop-up ------------------------------->
    const handleCloseUnsupportedIosBrowserModal = () => {
        dispatch(setIsIOSBrowserCheckModalOpen(false));
    };

    const unsupportedIosBrowserHeader = <h5 className='modal-title'>{localisedValues["unsupported_browser"]}</h5>;

    const unsupportedIosBrowserBody = (
        <div className='cms-modal-text'>
            <p>
                {localisedValues["this_browser_is_not_supported"]} {localisedValues["open_in_safari_chrome_or_install_mobile_app"]}
            </p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues["note_colon"]}</strong>{" "}
                    <span>{localisedValues["host_features_will_not_be_available_when_using_app"]}</span>
                </p>
            )}
        </div>
    );

    const unsupportedIosBrowserFooter = (
        <div className='d-flex align-items-center justify-content-between full-width'>
            <div className='button-inline more-space button-inline-tb-space d-flex'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={goToPlayStore}>
                        {localisedValues["download_app"]}
                    </button>
                </div>
            </div>
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={handleCloseUnsupportedIosBrowserModal}>
                        {localisedValues["cancel_initcap"]}
                    </button>
                </div>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={handleCopyLink}>
                        {localisedValues["copy_link"]}
                    </button>
                </div>
            </div>
        </div>
    );

    // <!----------------------- iOS unsupported broweser Pop-up ------------------------------->
    const handleCloseWebViewFoundModal = () => {
        dispatch(setIsWebViewFoundModalOpen(false));
    };

    const webViewFoundHeader = <h5 className='modal-title'>{localisedValues["unsupported_browser"]}</h5>;

    const webViewFoundBody = (
        <div className='cms-modal-text'>
            <p>{localisedValues["open_in_safari_chrome_or_install_mobile_app"]}</p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues["note_colon"]}</strong>{" "}
                    <span>{localisedValues["host_features_will_not_be_available_when_using_app"]}</span>
                </p>
            )}
        </div>
    );

    const webViewFoundFooter = (
        <div className='d-flex align-items-center justify-content-between full-width'>
            <div className='button-inline more-space button-inline-tb-space d-flex'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={goToPlayStore}>
                        {localisedValues["download_app"]}
                    </button>
                </div>
            </div>
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={handleCloseWebViewFoundModal}>
                        {localisedValues["okay_cap"]}
                    </button>
                </div>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={handleCopyLink}>
                        {localisedValues["copy_link"]}
                    </button>
                </div>
            </div>
        </div>
    );

    // <!----------------------- android unsupported browser Pop-up ------------------------------->
    const handleCloseAndroidUnsupportedBrowserModal = () => {
        dispatch(setIsAndroidBrowserCheckModalOpen(false));
    };

    const androidUnsupportedBrowserHeader = <h5 className='modal-title'>{localisedValues["unsupported_browser"]}</h5>;

    const androidUnsupportedBrowserBody = (
        <div className='cms-modal-text'>
            <p>
                {localisedValues["this_browser_is_not_supported"]} {localisedValues["open_in_chrome_or_install_mobile_app"]}
            </p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues["note_colon"]}</strong>{" "}
                    <span>{localisedValues["host_features_will_not_be_available_when_using_app"]}</span>
                </p>
            )}
        </div>
    );

    const androidUnsupportedBrowserFooter = (
        <div className='d-flex align-items-center justify-content-between full-width'>
            <div className='button-inline more-space button-inline-tb-space d-flex'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={goToPlayStore}>
                        {localisedValues["download_app"]}
                    </button>
                </div>
            </div>
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={handleCloseAndroidUnsupportedBrowserModal}>
                        {localisedValues["cancel_initcap"]}
                    </button>
                </div>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={handleCopyLink}>
                        {localisedValues["copy_link"]}
                    </button>
                </div>
            </div>
        </div>
    );

    // <!----------------------- android WebView recommendation Pop-up ------------------------------->
    const handleContinueAnyway = () => {
        // Set flag so checkSupportedBrowsers() won't show modal again
        dispatch(setHasUserAcceptedWebView(true));
        dispatch(setIsAndroidWebViewModalOpen(false));
        // Signal CallScreens to auto-accept privacy policy and continue normal flow
        // This triggers handlePrivacyPolicyAgreed(true) → checkForGuestName() → callJoinMeetingAPI()
        dispatch(setShouldAutoAcceptPrivacyPolicy(true));
    };

    const handleOpenInBrowser = () => {
        const currentUrl = window.location.href;

        // Copy link as fallback
        CopyTextOrLinkToClipboard(currentUrl, localisedValues?.link_copied_open_in_browser);

        // Try Android Intent to open in Chrome
        const urlWithoutProtocol = currentUrl.replace(/^https?:\/\//, "");
        const intentUrl = `intent://${urlWithoutProtocol}#Intent;scheme=https;package=com.android.chrome;end`;

        // Try to open Chrome via Intent
        window.location.href = intentUrl;

        // Close modal
        dispatch(setIsAndroidWebViewModalOpen(false));
    };

    const androidWebViewHeader = <h5 className='modal-title'>{localisedValues["open_in_browser"]}</h5>;

    const androidWebViewBody = (
        <div className='cms-modal-text'>
            <p>{localisedValues["webview_limited_support_message"]}</p>
        </div>
    );

    const androidWebViewFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' data-bs-dismiss='modal' onClick={handleContinueAnyway}>
                    {localisedValues["continue_anyway"]}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={handleOpenInBrowser}>
                    {localisedValues["open_in_browser"]}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* <!----------------------- web unsupported browser Pop-up -------------------------------> */}
            <CenterModal
                showModal={isUnSupportedBrowserModalOpen}
                closeModal={handleCloseUnsupportedBrowserModal}
                modalClass='default-modal modal-medium mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={header}
                body={body}
                footer={footer}
                backdrop={"static"}
            />

            {/* <!----------------------- iOS & android lower version Pop-up -------------------------------> */}
            <CenterModal
                showModal={isLowerVersionModalOpen}
                closeModal={handleCloseLowerVersionModal}
                modalClass='default-modal modal-medium mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={lowerVrsionHeader}
                body={lowerVersionBody}
                footer={lowerVersionFooter}
                backdrop={"static"}
            />

            {/* <!----------------------- iOS unsupported broweser Pop-up -------------------------------> */}
            <CenterModal
                showModal={isIOSBrowserCheckModalOpen}
                closeModal={handleCloseUnsupportedIosBrowserModal}
                modalClass='default-modal modal-medium mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={unsupportedIosBrowserHeader}
                body={unsupportedIosBrowserBody}
                footer={unsupportedIosBrowserFooter}
                backdrop={"static"}
            />
            {/* <!----------------------- iOS(web-view) unsupported broweser Pop-up -------------------------------> */}
            <CenterModal
                showModal={isWebViewFoundModalOpen}
                closeModal={handleCloseWebViewFoundModal}
                modalClass='default-modal modal-medium mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={webViewFoundHeader}
                body={webViewFoundBody}
                footer={webViewFoundFooter}
                backdrop={"static"}
            />
            {/* <!----------------------- android unsupported browser Pop-up -------------------------------> */}
            <CenterModal
                showModal={isAndroidBrowserCheckModalOpen}
                closeModal={handleCloseAndroidUnsupportedBrowserModal}
                modalClass='default-modal modal-medium mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={androidUnsupportedBrowserHeader}
                body={androidUnsupportedBrowserBody}
                footer={androidUnsupportedBrowserFooter}
                backdrop={"static"}
            />
            {/* <!----------------------- android WebView recommendation Pop-up -------------------------------> */}
            <CenterModal
                showModal={isAndroidWebViewModalOpen}
                closeModal={handleContinueAnyway}
                modalClass='default-modal modal-medium mob-bottom-top android-webview-modal'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={androidWebViewHeader}
                body={androidWebViewBody}
                footer={androidWebViewFooter}
                backdrop={"static"}
            />
        </>
    );
};

export default UnSupportedBrowserModal;
